import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
    Box, Typography, Paper, CircularProgress, Divider, 
    IconButton, Alert, Grid, Card, CardContent, Button,useTheme
} from "@mui/material";
import { Edit as EditIcon, Add as AddIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import PhoneIcon from "@mui/icons-material/Phone";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { tokens } from "../../theme";
const Team = () => {
     const theme = useTheme();
        const colors = tokens(theme.palette.mode);
    const { id } = useParams();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

 useEffect(() => {
    const fetchEmployeeProfile = async () => {
        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem("emsUser")); 
            const email = user?.user?.email;

            if (!email) {
                setError("Email not found in localStorage.");
                setLoading(false);
                return;
            }

            const accessToken = JSON.parse(localStorage.getItem('emsUser'))?.user?.stsTokenManager?.accessToken;

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL_HRM}/get-employee-profile/${email}`,
                {
                    headers: {
                       'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            setProfileData(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to fetch employee profile");
            setLoading(false);
            console.error("Error fetching employee profile:", err);
        }
    };

    fetchEmployeeProfile();
}, []);

    const handleEditEmployee = () => {
        const user = JSON.parse(localStorage.getItem("emsUser")); 
        const email = user?.user?.email;
        localStorage.setItem("editEmployeeId", id);
        navigate(`/edit-employee/${email}`);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress sx={{color: colors.grey[100]}}/>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 4, mx: "auto", maxWidth: 500 }}>
                {error}
            </Alert>
        );
    }

    if (!profileData) {
        return (
            <Alert severity="warning" sx={{ mt: 4, mx: "auto", maxWidth: 500 }}>
                No employee data found
            </Alert>
        );
    }

    const {
        personal_details,
        address,
        bank_details,
        work_experience,
        emergency_contact,
        employee_documents,
        qualification_documents
    } = profileData;

    return (
        <Box sx={{
            flexGrow: 1,
            width: "100%",
            minHeight: "100vh",
            overflowY: "auto",
            p: 3,
            display: "flex",
            justifyContent: "center",
        }}>
            <Paper sx={{
                width: "90%",
                maxWidth: "1200px",
                padding: 4,
                backgroundColor: "#f9f9f9",
                display: "flex",
                flexDirection: "column",
            }}>
                {/* Title */}
                <Box sx={{ textAlign: "center", fontWeight: "bold", mb: 3, color: "#1976d2" }}>
                    <Typography variant="h2" fontWeight="bold">
                        {personal_details?.First_Name || "No Name Available"} 
                        <IconButton color="primary" sx={{ ml: 1 }} onClick={handleEditEmployee}>
                            <EditIcon />
                        </IconButton>
                    </Typography>
                    <Typography variant="subtitle1">
                        {personal_details?.Designation || ""}
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Personal Details */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderLeft: "5px solid #1976d2", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#1976d2" }}>
                                    <PersonIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Personal Details
                                </Typography>
                                <Typography><strong>Full Name:</strong> {`${personal_details?.First_Name || " "} ${personal_details?.Middle_Name || ""} ${personal_details?.Last_Name || " "}`}</Typography>
                                <Typography><strong>Legal Name:</strong> {personal_details?.Legal_Name || " "}</Typography>
                                <Typography><strong>Gender:</strong> {personal_details?.Gender || " "}</Typography>
                                <Typography><strong>Marital Status:</strong> {personal_details?.Marital_Status || " "}</Typography>
                                <Typography>
                                <strong>Date of Birth:</strong>{" "}
                                {personal_details?.Date_Of_Birth
                                    ? new Date(personal_details.Date_Of_Birth).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short',
                                        day: '2-digit',
                                    })
                                    : " "}
                                </Typography>
                                <Typography><strong>Blood Group:</strong> {personal_details?.Blood_Group || " "}</Typography>
                                <Typography><strong>Languages Known:</strong> {personal_details?.Languages_Known || " "}</Typography>
                                <Typography><strong>T-Shirt Size:</strong> {personal_details?.Tshirt_Size || " "}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Contact Details */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderLeft: "5px solid #28a745", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#28a745" }}>
                                    <PhoneIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Contact Details
                                </Typography>
                                <Typography><strong>Mobile Number:</strong> {personal_details?.Mobile_Number || " "}</Typography>
                                <Typography><strong>Personal Email:</strong> {personal_details?.Personal_Email_ID || " "}</Typography>
                                <Typography><strong>Corporate Email:</strong> {personal_details?.Corporate_Email_ID || " "}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Job Details */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderLeft: "5px solid #ff9800", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#ff9800" }}>
                                    <WorkIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Job Details
                                </Typography>
                                <Typography><strong>Job Title:</strong> {personal_details?.Designation || " "}</Typography>
                                <Typography><strong>Designation:</strong> {personal_details?.Designation || " "}</Typography>
                                <Typography><strong>Probationary Period:</strong> {personal_details?.Probationary_Period || " "}</Typography>
                                <Typography><strong>Branch:</strong> {personal_details?.Branch_ID || " "}</Typography>
                                <Typography>
                                <strong>Joining Date:</strong>{" "}
                                {(() => {
                                    const joiningDate = personal_details?.Joining_Date;
                                    if (!joiningDate) return " ";

                                    const parsedDate = new Date(Date.parse(joiningDate));
                                    return isNaN(parsedDate)
                                    ? "Invalid Date"
                                    : parsedDate.toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short',
                                        day: '2-digit',
                                        });
                                })()}
                                </Typography>

                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Bank Details */}
                    {bank_details && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ borderLeft: "5px solid #6C757D", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#6C757D" }}>
                                        <AccountBalanceIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Bank Details
                                    </Typography>
                                    <Typography><strong>UPI ID:</strong> {bank_details?.UPI_ID || " "}</Typography>
                                    <Typography><strong>Bank Account Number:</strong> {bank_details?.Bank_Account_Number || " "}</Typography>
                                    <Typography><strong>PAN Number:</strong> {bank_details?.PAN_Number || " "}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Address Details */}
                    {address && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ borderLeft: "5px solid #d32f2f", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#d32f2f" }}>
                                        <HomeIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Address Details
                                    </Typography>
                                    <Typography><strong>Present Address:</strong> {address?.Present_Address || " "}</Typography>
                                    <Typography><strong>Present City:</strong> {address?.Present_City || " "}</Typography>
                                    <Typography><strong>Country:</strong> {address?.Country || " "}</Typography>
                                    <Typography><strong>Permanent Address:</strong> {address?.Permanent_Address || " "}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Work Experience */}
                    {work_experience && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ borderLeft: "5px solid #009688", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#009688" }}>
                                        <WorkIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Work Experience
                                    </Typography>
                                    <Typography><strong>Professional Reference 1:</strong> {work_experience.professional_reference1 || " "}</Typography>
                                    <Typography><strong>Reference 1 Contact:</strong> {work_experience.professional_reference1_contact_number || " "}</Typography>
                                    <Typography><strong>Professional Reference 2:</strong> {work_experience.professional_reference2 || " "}</Typography>
                                    <Typography><strong>Reference 2 Contact:</strong> {work_experience.professional_reference2_contact_number || " "}</Typography>
                                    <Typography><strong>Skill Sets:</strong> {work_experience.skill_sets || " "}</Typography>
                                    {work_experience?.uploaded_files?.experience_certificate ? (
                                        <Typography>
                                            <strong>Experience Certificate:</strong>
                                            <a
                                                href={work_experience.uploaded_files.experience_certificate}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#1e88e5", textDecoration: "none", marginLeft: "5px" }}
                                            >
                                                View Certificate
                                            </a>
                                        </Typography>
                                    ) : (
                                        <Typography>No experience certificate uploaded.</Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Employee Documents */}
                    {employee_documents && Object.keys(employee_documents).length > 0 && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ borderLeft: "5px solid #673ab7", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#673ab7" }}>
                                        📄 Employee Documents
                                    </Typography>
                                    {Object.entries(employee_documents).map(([key, url]) => (
                                        <Typography key={key}>
                                            <strong>{key.replace("_", " ").toUpperCase()}:</strong>
                                            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1e88e5", textDecoration: "none", marginLeft: "5px" }}>
                                                View Document
                                            </a>
                                        </Typography>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Qualification Documents */}
                    {qualification_documents && Object.keys(qualification_documents).length > 0 && (
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderLeft: "5px solid #673ab7",
                                backgroundColor: "#ffffff",
                                height: "100%",
                                color: "black",
                            }}
                        >
                        <CardContent>
                            <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", mb: 2, color: "#673ab7" }}
                            >
                            🎓 Qualification Documents
                            </Typography>
                            {Object.entries(qualification_documents).map(([key, url]) => (
                            <Typography key={key} sx={{ mb: 1 }}>
                                <strong>{key.replace(/_/g, " ").toUpperCase()}:</strong>
                                <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: "#1e88e5",
                                    textDecoration: "none",
                                    marginLeft: "5px",
                                }}
                                >
                                View Document
                                </a>
                            </Typography>
                            ))}
                        </CardContent>
                        </Card>
                    </Grid>
                    )}



                    {/* Emergency Contact Details */}
                    {emergency_contact && Object.keys(emergency_contact).length > 0 && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ borderLeft: "5px solid #ff9800", backgroundColor: "#ffffff", height: "100%", color: "black" }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#ff9800" }}>
                                        <WorkIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Emergency Contact Details
                                    </Typography>
                                    <Typography><strong>Emergency Contact Name1:</strong> {emergency_contact?.emergency_contact_name1 || " "}</Typography>
                                    <Typography><strong>Emergency Contact Number1:</strong> {emergency_contact?.emergency_contact_number1 || " "}</Typography>
                                    <Typography><strong>Emergency Person Relation1:</strong> {emergency_contact?.emergency_person_relation1 || " "}</Typography>
                                    <Typography><strong>Emergency Contact Name2:</strong> {emergency_contact?.emergency_contact_name2 || " "}</Typography>
                                    <Typography><strong>Emergency Contact Number2:</strong> {emergency_contact?.emergency_contact_number2 || " "}</Typography>
                                    <Typography><strong>Emergency Person Relation2:</strong> {emergency_contact?.emergency_person_relation2 || " "}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Box>
    );
};

export default Team;